import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccount, useStore } from '@feathr/extender/state';
import type { IBaseAttributes, Model } from '@feathr/rachis';

import type { IAttrOption, TOptionsGroup } from './Predicates/utils';
import { getActivities, getAttributes } from './Predicates/utils';

export interface IPredicateOptions {
  activities: IAttrOption[];
  attributes: IAttrOption[];
  /** True until the option lists for the current model/optionsGroup have resolved. */
  isLoading: boolean;
}

/**
 * Loads the attribute and activity field options for a model's predicate editor
 * once for the whole list. Deferred until any in-flight document fetch finishes,
 * so the fetch-gated options (read-only, global-message condition) reflect the
 * loaded doc rather than the placeholder. Callers must be `observer`s so the
 * pending-state change triggers a re-render.
 */
export function usePredicateOptions(
  model: Model<IBaseAttributes>,
  optionsGroup: TOptionsGroup,
): IPredicateOptions {
  const { t } = useTranslation();
  const { isAnyFeathrPlatform, isPending: isAccountPending } = useAccount();
  const { CustomFields } = useStore();

  const [attributes, setAttributes] = useState<IAttrOption[]>([]);
  const [activities, setActivities] = useState<IAttrOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    if (model.isPending || isAccountPending) {
      return;
    }

    let active = true;

    async function load(): Promise<void> {
      // Meta and goal predicates don't offer person attributes.
      const skipAttributes = ['facebook', 'goal'].some((group) =>
        optionsGroup.toLowerCase().includes(group),
      );
      const [loadedAttributes, loadedActivities] = await Promise.all([
        skipAttributes ? Promise.resolve<IAttrOption[]>([]) : getAttributes(t, CustomFields),
        getActivities({ CustomFields, model, isAnyFeathrPlatform, optionsGroup, t }),
      ]);
      if (active) {
        setAttributes(loadedAttributes);
        setActivities(loadedActivities);
        setIsLoading(false);
      }
    }

    load();
    return (): void => {
      active = false;
    };
  }, [model, optionsGroup, isAnyFeathrPlatform, model.isPending, isAccountPending]);

  return { activities, attributes, isLoading };
}
